import { useEffect, useRef, RefObject } from 'react';

/**
 * Hook that triggers a callback when clicking outside the referenced element
 *
 * @param callback - Function to call when clicking outside the element
 * @returns RefObject to attach to the element you want to detect clicks outside of
 *
 * @example
 * const dropdownRef = useClickOutside<HTMLDivElement>(() => setOpen(false));
 * return <div ref={dropdownRef}>...</div>
 */
export function useClickOutside<T extends HTMLElement>(
  callback: () => void
): RefObject<T> {
  const ref = useRef<T>(null);
  const callbackRef = useRef(callback);

  // Keep callback ref up to date to avoid stale closures
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callbackRef.current();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return ref;
}
